import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  FiSend,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiUser,
  FiPhone,
  FiMapPin,
  FiInfo
} from "react-icons/fi";
import { MdElectricCar } from "react-icons/md";

const SendSimpleLeadModal = ({ isOpen, onClose, lead, onLeadSentSuccess, onLeadSentFailure }) => {
  const [stage, setStage] = useState("confirm"); // 'confirm' | 'sending' | 'success' | 'error'
  const [apiResponse, setApiResponse] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  if (!isOpen || !lead) return null;

  const handleClose = () => {
    // If sending is in progress, prevent closing
    if (stage === "sending") return;

    const sentSuccess = stage === "success";
    const sentError = stage === "error";
    const lastResponse = apiResponse;
    const lastErrorMsg = errorMessage;

    setStage("confirm");
    setApiResponse(null);
    setErrorMessage("");
    onClose();

    if (sentSuccess && onLeadSentSuccess) {
      onLeadSentSuccess(lastResponse);
    } else if (sentError && onLeadSentFailure) {
      onLeadSentFailure(lastResponse || { message: lastErrorMsg });
    }
  };

  const handleConfirmSend = async () => {
    setStage("sending");
    setApiResponse(null);
    setErrorMessage("");

    const startTime = Date.now();

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}api/simple-leads/send-lead`,
        {
          leadId: lead._id,
          // Vehicle (model) selected by the call center; backend picks a random colour. Empty = fully automatic
          ...(lead.simple_selected_model_id ? { modelId: lead.simple_selected_model_id } : {})
        }
      );

      // Ensure loading state displays smoothly
      const elapsed = Date.now() - startTime;
      if (elapsed < 600) {
        await new Promise((r) => setTimeout(r, 600 - elapsed));
      }

      if (res.data?.success) {
        setApiResponse(res.data);
        setStage("success");
      } else {
        setApiResponse(res.data);
        setErrorMessage(res.data?.message || "Failed to dispatch lead to Simple Energy.");
        setStage("error");
      }
    } catch (err) {
      const elapsed = Date.now() - startTime;
      if (elapsed < 600) {
        await new Promise((r) => setTimeout(r, 600 - elapsed));
      }

      const errData = err.response?.data;
      setApiResponse(errData);
      setErrorMessage(
        errData?.message ||
        (typeof errData?.result === "string" ? errData.result : null) ||
        err.message ||
        "Error communicating with Simple Energy API."
      );
      setStage("error");
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        {/* Backdrop click to close if not sending */}
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        />

        {/* Modal Window */}
        <motion.div
          className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 z-10 flex flex-col p-6"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ type: "spring", duration: 0.35, bounce: 0.15 }}
        >
          {/* Close Button in Top Right */}
          {stage !== "sending" && (
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors z-20 cursor-pointer"
            >
              <FiX className="w-5 h-5" />
            </button>
          )}

          {/* Modal Body: Switch between stages */}
          <div>
            {/* STAGE 1: CONFIRMATION */}
            {stage === "confirm" && (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="flex flex-col gap-4"
              >
                {/* Header Icon & Title */}
                <div className="text-center pt-1">
                  <div className="w-12 h-12 mx-auto rounded-full bg-[#e5f3fb] flex items-center justify-center mb-2.5 text-[#487192] shadow-sm">
                    <FiSend className="w-5 h-5 translate-x-0.5" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-800">Ready to Send Lead?</h4>
                  <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                    Please review customer details before submitting this enquiry to Simple Energy.
                  </p>
                </div>

                {/* Lead Summary Info Card */}
                <div className="bg-[#f8fafc] border border-gray-200/70 rounded-xl p-3.5 flex flex-col gap-2.5 text-xs text-gray-700">
                  <div className="flex items-center justify-between pb-2 border-b border-gray-200/60">
                    <span className="flex items-center gap-1.5 font-medium text-gray-500">
                      <MdElectricCar className="text-[#487192]" /> Vehicle
                    </span>
                    {lead.simple_selected_model_id && lead.simple_selected_model ? (
                      <span className="font-bold text-gray-900 text-right">
                        {lead.simple_selected_model}
                        <span className="ml-1.5 font-medium text-gray-500">(colour auto)</span>
                      </span>
                    ) : (
                      <span className="bg-gray-100 text-gray-600 border border-gray-200 font-semibold px-2 py-0.5 rounded-full text-[10px]">
                        Auto-assigned
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between pb-2 border-b border-gray-200/60">
                    <span className="flex items-center gap-1.5 font-medium text-gray-500">
                      <FiUser className="text-[#487192]" /> Customer Name
                    </span>
                    <span className="font-bold text-gray-900">{lead.name || "N/A"}</span>
                  </div>

                  <div className="flex items-center justify-between pb-2 border-b border-gray-200/60">
                    <span className="flex items-center gap-1.5 font-medium text-gray-500">
                      <FiPhone className="text-[#487192]" /> Mobile Number
                    </span>
                    <span className="font-bold text-gray-900 font-mono">{lead.mobile || "N/A"}</span>
                  </div>

                  <div className="flex items-center justify-between pb-2 border-b border-gray-200/60">
                    <span className="flex items-center gap-1.5 font-medium text-gray-500">
                      <FiMapPin className="text-[#487192]" /> Pincode
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900 font-mono">{lead.pincode || "N/A"}</span>
                      {lead.simple_pincode_verified === 1 ? (
                        <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold px-2 py-0.5 rounded-full text-[10px]">
                          Verified
                        </span>
                      ) : (
                        <span className="bg-red-50 text-red-700 border border-red-200 font-semibold px-2 py-0.5 rounded-full text-[10px]">
                          Unverified
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 font-medium text-gray-500">
                      <FiCheckCircle className={
                        (lead.simple_call_verification === 1) ? "text-emerald-600" :
                        (lead.simple_call_verification === 2) ? "text-red-600" :
                        (lead.simple_call_verification === 3) ? "text-blue-600" :
                        "text-gray-400"
                      } /> Call Verification
                    </span>
                    {(() => {
                      const callVal = lead.simple_call_verification ?? 0;
                      const callMap = {
                        1: { label: "Interested", badgeClass: "bg-accepted text-white" },
                        2: { label: "Not Interested", badgeClass: "bg-danger text-white" },
                        3: { label: "Schedule", badgeClass: "bg-[#2563eb] text-white" },
                        0: { label: "Pending", badgeClass: "bg-gray-400 text-white" }
                      };
                      const curr = callMap[callVal] || callMap[0];
                      return (
                        <span className={`${curr.badgeClass} font-semibold px-2.5 py-0.5 rounded-global text-[11px] shadow-sm`}>
                          {curr.label}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Subtle Notice Callout */}
                <div className="flex items-start gap-2 bg-amber-50/70 border border-amber-200/60 rounded-lg p-2.5 text-[11px] text-amber-800">
                  <FiInfo className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                  <span>
                    Once sent, this lead will be dispatched to Simple Energy's API with automated dealer allocation.
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2.5 pt-1">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 py-2 px-3 rounded-xl border border-gray-300 font-semibold text-xs text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={lead.simple_pincode_verified !== 1}
                    onClick={handleConfirmSend}
                    className={`flex-1 py-2 px-3 rounded-xl font-semibold text-xs shadow-sm transition-all flex items-center justify-center gap-1.5 ${
                      lead.simple_pincode_verified === 1
                        ? "bg-[#487192] hover:bg-[#385873] text-white active:scale-[0.98] cursor-pointer"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed border border-gray-300"
                    }`}
                  >
                    <FiSend className="w-3.5 h-3.5" />
                    Confirm & Send Lead
                  </button>
                </div>
              </motion.div>
            )}

            {/* STAGE 2: LOADING / SENDING */}
            {stage === "sending" && (
              <motion.div
                key="sending"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="py-8 flex flex-col items-center justify-center text-center gap-4"
              >
                {/* Advanced Pulsing Loader */}
                <div className="relative flex items-center justify-center">
                  <motion.div
                    className="absolute w-20 h-20 rounded-full bg-[#487192]/20"
                    animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                  />

                  {/* Dual Gradient Spinning Ring */}
                  <motion.div
                    className="w-14 h-14 rounded-full border-3 border-t-[#487192] border-r-emerald-500 border-b-gray-200 border-l-transparent shadow-md"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.85, repeat: Infinity, ease: "linear" }}
                  />

                  {/* Central Icon */}
                  <div className="absolute flex items-center justify-center">
                    <MdElectricCar className="w-6 h-6 text-[#487192] animate-pulse" />
                  </div>
                </div>

                <div className="flex flex-col gap-1 mt-1">
                  <h4 className="text-base font-bold text-gray-800">
                    Sending Lead to Simple Energy...
                  </h4>
                  <p className="text-xs text-gray-500">
                    Communicating with OEM gateway & verifying dispatch
                  </p>
                </div>

                {/* Progress bar */}
                <div className="w-44 h-1.5 bg-gray-100 rounded-full overflow-hidden shadow-inner">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#487192] to-emerald-500 rounded-full"
                    animate={{ x: ["-100%", "100%"] }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                  />
                </div>
              </motion.div>
            )}

            {/* STAGE 3: SUCCESS */}
            {stage === "success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex flex-col items-center text-center gap-3.5 py-2"
              >
                {/* Animated Checkmark */}
                <div className="relative flex items-center justify-center">
                  <motion.div
                    className="absolute w-16 h-16 rounded-full bg-emerald-100"
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.1, 1] }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  />
                  <svg className="w-16 h-16 relative z-10" viewBox="0 0 64 64">
                    <motion.circle
                      cx="32"
                      cy="32"
                      r="26"
                      stroke="#10b981"
                      strokeWidth="3.5"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                    <motion.path
                      d="M20 33l8 8 16-16"
                      stroke="#10b981"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.3, duration: 0.3, ease: "easeOut" }}
                    />
                  </svg>
                </div>

                <div className="flex flex-col gap-0.5">
                  <h4 className="text-base font-bold text-gray-800">Lead Dispatched Successfully!</h4>
                  <p className="text-xs text-gray-500">
                    Simple Energy has received and accepted this enquiry.
                  </p>
                </div>

                {/* API Response Message Box */}
                <div className="w-full bg-emerald-50 border border-emerald-200/70 rounded-xl p-3 text-left flex flex-col gap-1 shadow-sm">
                  <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-[11px] uppercase tracking-wider">
                    <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                    Simple Energy Response
                  </div>
                  <div className="bg-white/80 border border-emerald-100 rounded-lg p-2 text-xs text-emerald-900 font-medium">
                    {apiResponse?.message || "Lead sent successfully to Simple Energy"}
                  </div>
                </div>

                {/* Done Button */}
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full mt-1 py-2 px-4 rounded-xl bg-[#487192] hover:bg-[#385873] text-white font-semibold text-xs shadow-sm active:scale-[0.98] transition-all cursor-pointer"
                >
                  Done
                </button>
              </motion.div>
            )}

            {/* STAGE 4: ERROR / REJECTED */}
            {stage === "error" && (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex flex-col items-center text-center gap-3.5 py-2"
              >
                {/* Animated Cross */}
                <div className="relative flex items-center justify-center">
                  <motion.div
                    className="absolute w-16 h-16 rounded-full bg-red-100"
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.1, 1] }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  />
                  <svg className="w-16 h-16 relative z-10" viewBox="0 0 64 64">
                    <motion.circle
                      cx="32"
                      cy="32"
                      r="26"
                      stroke="#ef4444"
                      strokeWidth="3.5"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                    />
                    <motion.path
                      d="M22 22l20 20M42 22l-20 20"
                      stroke="#ef4444"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.3, duration: 0.3, ease: "easeOut" }}
                    />
                  </svg>
                </div>

                <div className="flex flex-col gap-0.5">
                  <h4 className="text-base font-bold text-gray-800">
                    {apiResponse?.duplicate ? "Duplicate Lead" : "Dispatch Rejected / Failed"}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {apiResponse?.duplicate
                      ? "This number already exists for Simple Energy, so it was not sent again."
                      : "Simple Energy API could not process this enquiry."}
                  </p>
                </div>

                {/* API Response Message Box */}
                <div className="w-full bg-red-50 border border-red-200/70 rounded-xl p-3 text-left flex flex-col gap-1 shadow-sm">
                  <div className="flex items-center gap-1.5 text-red-800 font-bold text-[11px] uppercase tracking-wider">
                    <FiAlertCircle className="w-3.5 h-3.5 text-red-600" />
                    Error Response
                  </div>
                  <div className="bg-white/80 border border-red-100 rounded-lg p-2 text-xs text-red-900 font-medium">
                    {errorMessage || apiResponse?.message || "Failed to send lead to Simple Energy"}
                  </div>
                </div>

                {/* Close Button */}
                <button
                  type="button"
                  onClick={handleClose}
                  className="w-full mt-1 py-2 px-4 rounded-xl bg-gray-700 hover:bg-gray-800 text-white font-semibold text-xs shadow-sm active:scale-[0.98] transition-all cursor-pointer"
                >
                  Close
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SendSimpleLeadModal;
